import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { PlatformSettingsResponse, TestMailResult } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { EMPTY_MAIL_FIELDS, MailConnectionFields, type MailFieldsState } from '../components/MailConnectionFields';
import { PlatformTabs } from '../components/PlatformTabs';

const SOURCE_LABEL: Record<TestMailResult['source'], string> = {
  organization: 'vereinsindividuelle Konfiguration',
  platform: 'globale Konfiguration',
  env: 'Server-Umgebungsvariablen (noch nichts hier gespeichert)',
};

export function PlatformMailPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'settings'],
    queryFn: () => api<PlatformSettingsResponse>('/platform/settings'),
  });

  const [fields, setFields] = useState<MailFieldsState>(EMPTY_MAIL_FIELDS);
  useEffect(() => {
    if (!data) return;
    setFields({
      transport: data.mail.transport,
      host: data.mail.host ?? '',
      port: data.mail.port,
      secure: data.mail.secure,
      user: data.mail.user ?? '',
      password: '',
      apiUrl: data.mail.apiUrl ?? '',
      apiKey: '',
      tenantId: data.mail.tenantId ?? '',
      clientId: data.mail.clientId ?? '',
      clientSecret: '',
      clientSecretExpiresAt: data.mail.clientSecretExpiresAt ?? '',
      graphSaveToSentItems: data.mail.graphSaveToSentItems,
      fromAddress: data.mail.fromAddress ?? '',
      fromName: data.mail.fromName ?? '',
    });
  }, [data]);

  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api<PlatformSettingsResponse>('/platform/settings', {
        method: 'PATCH',
        body: {
          mail: {
            transport: fields.transport,
            host: fields.host || undefined,
            port: fields.port,
            secure: fields.secure,
            user: fields.user || undefined,
            password: fields.password || undefined,
            apiUrl: fields.apiUrl || undefined,
            apiKey: fields.apiKey || undefined,
            tenantId: fields.tenantId || undefined,
            clientId: fields.clientId || undefined,
            clientSecret: fields.clientSecret || undefined,
            clientSecretExpiresAt: fields.clientSecretExpiresAt || undefined,
            graphSaveToSentItems: fields.graphSaveToSentItems,
            fromAddress: fields.fromAddress || undefined,
            fromName: fields.fromName || undefined,
          },
        },
      }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setFields((f) => ({ ...f, password: '', apiKey: '', clientSecret: '' }));
      setTimeout(() => setSaved(false), 2000);
      void qc.invalidateQueries({ queryKey: ['platform', 'settings'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const [testTo, setTestTo] = useState('');
  const [testResult, setTestResult] = useState<TestMailResult | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const sendTest = useMutation({
    mutationFn: () => api<TestMailResult>('/platform/settings/mail/test', { method: 'POST', body: { to: testTo } }),
    onSuccess: (r) => {
      setTestError(null);
      setTestResult(r);
    },
    onError: (e) => {
      setTestResult(null);
      setTestError(e instanceof ApiError ? e.message : 'Testmail konnte nicht verschickt werden.');
    },
  });

  return (
    <div className="space-y-4">
      <PlatformTabs />
      <h1 className="hidden text-xl font-semibold md:block">SMTP Server</h1>
      <p className="-mt-2 text-sm text-slate-500">
        Globale Standard-Konfiguration für den Mailversand (z. B. Passwort-zurücksetzen-Links) —
        gilt für jeden Verein, der keine eigene Konfiguration hinterlegt hat (siehe „SMTP Server"
        in der Vereinsadministration).
      </p>

      <Card title="Server">
        {isLoading ? (
          <p className="text-sm text-slate-400">Lädt…</p>
        ) : (
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
          >
            <MailConnectionFields
              value={fields}
              onChange={setFields}
              hasPassword={data?.mail.hasPassword ?? false}
              hasApiKey={data?.mail.hasApiKey ?? false}
              hasClientSecret={data?.mail.hasClientSecret ?? false}
            />
            <div className="flex items-center gap-2">
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Speichert…' : 'Speichern'}
              </Button>
              {saved && <span className="text-sm text-green-600">Gespeichert ✓</span>}
            </div>
            {error && <Alert>{error}</Alert>}
          </form>
        )}
      </Card>

      <Card title="Testmail verschicken">
        <p className="mb-3 text-sm text-slate-500">
          Verschickt eine kurze Testmail über die aktuell gespeicherte Konfiguration (bzw. den
          Server-Fallback, falls hier noch nichts gespeichert ist) — so lässt sich prüfen, ob die
          Zugangsdaten stimmen und die Mail beim Empfänger ankommt, ohne dafür extra ein Passwort
          zurücksetzen zu müssen.
        </p>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setTestResult(null);
            sendTest.mutate();
          }}
        >
          <Field label="Empfänger">
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} required className="w-64" />
          </Field>
          <Button type="submit" disabled={!testTo.trim() || sendTest.isPending}>
            {sendTest.isPending ? 'Wird verschickt…' : 'Testmail verschicken'}
          </Button>
        </form>
        {testError && (
          <div className="mt-3">
            <Alert>{testError}</Alert>
          </div>
        )}
        {testResult && (
          <div className="mt-3">
            {testResult.sent ? (
              <Alert kind="success">
                Testmail verschickt (über {SOURCE_LABEL[testResult.source]}) — bitte Posteingang und Spam-Ordner
                prüfen.
              </Alert>
            ) : (
              <Alert>
                Fehlgeschlagen (über {SOURCE_LABEL[testResult.source]}): {testResult.error}
              </Alert>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
